import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/useRoles';
import { Permission, PermissionValue } from '@/types';
import { AlertTriangle, Loader2 } from 'lucide-react';

interface PermissionGuardProps {
  children: ReactNode;
  requiredPermission?: PermissionValue;
  requiredPermissions?: PermissionValue[];
  requireAll?: boolean; // If true, user must have ALL permissions. If false, ANY permission is enough
  fallback?: ReactNode;
  redirectTo?: string;
}

export function PermissionGuard({
  children,
  requiredPermission,
  requiredPermissions,
  requireAll = false,
  fallback,
  redirectTo,
}: PermissionGuardProps) {
  const { user } = useAuth();
  const { hasPermission, loading } = usePermissions(user?.roles || []);

  // Show loading spinner while permissions are being fetched
  if (loading) {
    return (
      <div className="min-h-screen min-h-dvh flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Build list of required permissions
  const permissions: PermissionValue[] = [];
  if (requiredPermission) permissions.push(requiredPermission);
  if (requiredPermissions) permissions.push(...requiredPermissions);

  // Check if user has required permissions
  const hasAccess = requireAll
    ? permissions.every((p) => hasPermission(p))
    : permissions.some((p) => hasPermission(p));

  if (!hasAccess) {
    // Redirect if specified
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }

    // Show custom fallback if provided
    if (fallback) {
      return <>{fallback}</>;
    }

    // Default unauthorized message
    return (
      <div className="min-h-screen min-h-dvh bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-200 p-8 text-center space-y-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-slate-900">Access Denied</h2>
            <p className="text-slate-600">
              You don't have permission to access this page. Please contact your administrator if you believe this is an error.
            </p>
          </div>
          <div className="pt-4">
            <a
              href="/dashboard"
              className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all"
            >
              Back to Dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
